import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth, apiFetch } from "@/context/AuthContext";

export default function Auth({ mode = "login" }) {
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const { login, register, loginGoogle } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");

  useEffect(() => {
    apiFetch("/auth/google/config", { auth: false })
      .then((c) => setGoogleEnabled(!!c.enabled))
      .catch(() => setGoogleEnabled(false));
  }, []);

  const startGoogle = async () => {
    try { await loginGoogle(); }
    catch (err) { toast.error(err.message); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isLogin) await login(email, password);
      else await register(email, password, name);
      toast.success("Welcome to ArcaCentum!");
      navigate(next && next.startsWith("/") ? next : "/studio", { replace: true });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative px-6">
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-1/4 w-[36rem] h-[36rem] bg-[#0055FF]/15 blur-[130px] rounded-full" />
      <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#0a0a0a] p-8">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <div className="w-9 h-9 rounded-xl bg-[#0055FF] flex items-center justify-center font-display font-extrabold text-lg">A</div>
          <span className="font-display text-xl font-bold">ArcaCentum<span className="text-[#00F0FF]">.ai</span></span>
        </Link>
        <h1 className="font-display font-bold text-3xl mb-2" data-testid="auth-title">{isLogin ? t("auth.loginTitle") : t("auth.signupTitle")}</h1>
        <p className="text-slate-500 text-sm mb-6">{isLogin ? t("auth.loginSubtitle") : t("auth.signupSubtitle")}</p>

        {googleEnabled && (
          <>
            <button onClick={startGoogle} className="w-full flex items-center justify-center gap-2 bg-white text-black rounded-xl py-3 font-semibold hover:bg-[#00F0FF] transition-colors duration-300 mb-4" data-testid="auth-google-btn">
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              {t("auth.google")}
            </button>
            <div className="flex items-center gap-3 my-4 text-slate-600 text-xs"><div className="flex-1 h-px bg-white/10" /> {t("auth.orContinue")} <div className="flex-1 h-px bg-white/10" /></div>
          </>
        )}

        <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
          {!isLogin && (
            <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl px-4 focus-within:border-[#0055FF] transition-colors duration-300">
              <User size={16} className="text-slate-500" />
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.name")} className="flex-1 bg-transparent outline-none py-3 text-white" data-testid="auth-name" />
            </div>
          )}
          <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl px-4 focus-within:border-[#0055FF] transition-colors duration-300">
            <Mail size={16} className="text-slate-500" />
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.email")} className="flex-1 bg-transparent outline-none py-3 text-white" data-testid="auth-email" />
          </div>
          <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl px-4 focus-within:border-[#0055FF] transition-colors duration-300">
            <Lock size={16} className="text-slate-500" />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.password")} className="flex-1 bg-transparent outline-none py-3 text-white" data-testid="auth-password" />
          </div>
          <button type="submit" disabled={busy} className="w-full bg-[#0055FF] text-white rounded-xl py-3.5 font-semibold shadow-[0_0_30px_rgba(0,85,255,0.45)] disabled:opacity-40 hover:-translate-y-0.5 transition-transform duration-300" data-testid="auth-submit">
            {busy ? <Loader2 size={18} className="animate-spin mx-auto" /> : isLogin ? t("auth.loginCta") : t("auth.signupCta")}
          </button>
        </form>

        <p className="text-slate-500 text-sm mt-6 text-center">
          {isLogin ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
          <button onClick={() => setIsLogin(!isLogin)} className="text-[#00F0FF] hover:underline" data-testid="auth-toggle">{isLogin ? t("auth.toSignup") : t("auth.toLogin")}</button>
        </p>
      </div>
    </div>
  );
}
